import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { ArrowLeft, Search, AlertCircle, ShoppingCart, Calendar, DollarSign, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCompras = ({ user }) => {
  const navigate = useNavigate();
  const [clienteId, setClienteId] = useState('');
  const [cpfBusca, setCpfBusca] = useState('');
  const [compras, setCompras] = useState([]);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAdmin = user?.cpf?.replace(/\D/g, '') === '00000000000';

  if (!user?.isLoggedIn || !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Acesso Restrito</h2>
            <p className="text-gray-600">Área exclusiva para administradores.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const buscarPorId = async () => {
    if (!clienteId.trim()) {
      setError('Informe o ID do cliente');
      return;
    }
    await buscarCompras(clienteId.trim());
  };

  const buscarPorCpf = async () => {
    if (!cpfBusca.trim()) {
      setError('Informe o CPF do cliente');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const cpfLimpo = cpfBusca.replace(/\D/g, '');
      const response = await axios.get(`${API}/knorr/clientes/buscar-por-cpf/${cpfLimpo}`, { timeout: 30000 });
      if (response.data?.id) {
        setClienteId(response.data.id.toString());
        setClienteInfo({ nome: response.data.nome, id: response.data.id });
        await buscarCompras(response.data.id.toString());
      }
    } catch (err) {
      setError('Cliente não encontrado com esse CPF.');
      setLoading(false);
    }
  };

  const buscarCompras = async (id) => {
    try {
      setLoading(true);
      setError('');
      setCompras([]);

      const response = await axios.get(`${API}/knorr/clientes/${id}/compras-vendas`, { timeout: 30000 });
      const data = response.data || [];
      setCompras(data);

      if (data.length > 0 && !clienteInfo) {
        setClienteInfo({ nome: data[0].nomexx, id: id, cpf: data[0].cpfxxx });
      }

      if (data.length === 0) {
        setError('Nenhuma compra encontrada para este cliente.');
      }
    } catch (err) {
      setError('Erro ao buscar compras do cliente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Agrupar por leilão
  const comprasPorLeilao = compras.reduce((acc, compra) => {
    const key = compra.idleilao;
    if (!acc[key]) {
      acc[key] = { idleilao: compra.idleilao, leilao: compra.leilao, datlei: compra.datlei, itens: [] };
    }
    acc[key].itens.push(compra);
    return acc;
  }, {});

  const leiloesAgrupados = Object.values(comprasPorLeilao).sort((a, b) =>
    new Date(b.datlei) - new Date(a.datlei)
  );

  const totalGeral = compras.reduce((sum, c) => sum + (c.valorpagar || 0), 0);
  const totalComissao = compras.reduce((sum, c) => sum + (c.valorcomissao || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-black mb-2 text-[rgb(25,42,86)]" data-testid="page-title">
              COMPRAS DE CLIENTES
            </h1>
            <p className="text-lg opacity-80 font-semibold">Consultar compras e vendas por cliente</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Busca */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-[rgb(25,42,86)] flex items-center">
              <Search className="h-5 w-5 mr-2" />
              Buscar Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Busca por ID */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Por ID do Cliente</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                    placeholder="Ex: 92437"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(25,42,86)] focus:border-transparent"
                    data-testid="input-cliente-id"
                    onKeyDown={(e) => e.key === 'Enter' && buscarPorId()}
                  />
                  <Button onClick={buscarPorId} className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold" data-testid="btn-buscar-id">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Busca por CPF */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Por CPF</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={cpfBusca}
                    onChange={(e) => setCpfBusca(e.target.value)}
                    placeholder="Ex: 018.617.070-02"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(25,42,86)] focus:border-transparent"
                    data-testid="input-cpf"
                    onKeyDown={(e) => e.key === 'Enter' && buscarPorCpf()}
                  />
                  <Button onClick={buscarPorCpf} className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold" data-testid="btn-buscar-cpf">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[rgb(25,42,86)]"></div>
          </div>
        )}

        {/* Error */}
        {error && (
          <Alert className="border-[rgb(238,29,37)] bg-red-50 mb-6">
            <AlertCircle className="h-4 w-4 text-[rgb(238,29,37)]" />
            <AlertDescription className="text-[rgb(238,29,37)]">{error}</AlertDescription>
          </Alert>
        )}

        {/* Resultado */}
        {!loading && compras.length > 0 && (
          <div className="space-y-6">
            {/* Info do cliente */}
            {clienteInfo && (
              <Card className="border-l-4 border-l-[rgb(25,42,86)]">
                <CardContent className="p-4 flex items-center space-x-4">
                  <User className="h-8 w-8 text-[rgb(25,42,86)]" />
                  <div>
                    <p className="font-bold text-[rgb(25,42,86)] text-lg">{clienteInfo.nome || compras[0]?.nomexx}</p>
                    <p className="text-sm text-gray-600">
                      ID: {clienteInfo.id || clienteId} | CPF: {clienteInfo.cpf || compras[0]?.cpfxxx}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-600">Total Lotes</p>
                  <p className="text-2xl font-black text-[rgb(25,42,86)]">{compras.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-600">Leilões</p>
                  <p className="text-2xl font-black text-[rgb(25,42,86)]">{leiloesAgrupados.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-600">Valor Total</p>
                  <p className="text-xl font-black text-[rgb(238,29,37)]">{formatCurrency(totalGeral)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-600">Comissão Total</p>
                  <p className="text-xl font-black text-green-600">{formatCurrency(totalComissao)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabelas por leilão */}
            {leiloesAgrupados.map((grupo) => (
              <Card key={grupo.idleilao} className="overflow-hidden">
                <CardHeader className="bg-[rgb(25,42,86)] text-white py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-bold flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {grupo.leilao}
                    </CardTitle>
                    <Badge className="bg-white text-[rgb(25,42,86)] font-bold text-xs">
                      {formatDate(grupo.datlei)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Lote</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Descrição</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">Qtd</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Parcela</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Total</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Comissão</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">Pagamento</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {grupo.itens.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono font-bold text-[rgb(25,42,86)]">{item.lotexx}</td>
                            <td className="px-3 py-2 text-gray-800 max-w-xs truncate">{item.deslot}</td>
                            <td className="px-3 py-2 text-center">{item.qtdxxx}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.vlrpar)}</td>
                            <td className="px-3 py-2 text-right font-semibold">{formatCurrency(item.valorpagar)}</td>
                            <td className="px-3 py-2 text-right text-green-700">{formatCurrency(item.valorcomissao)}</td>
                            <td className="px-3 py-2 text-center text-xs">{item.forma_pagamento || '-'}</td>
                            <td className="px-3 py-2 text-center">
                              <Badge className={item.st_parcela === 'A' ? 'bg-yellow-100 text-yellow-800 text-xs' : 'bg-green-100 text-green-800 text-xs'}>
                                {item.st_parcela === 'A' ? 'Aberta' : 'Paga'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2">
                        <tr>
                          <td colSpan="4" className="px-3 py-2 text-right font-bold text-gray-700">Subtotal:</td>
                          <td className="px-3 py-2 text-right font-black text-[rgb(238,29,37)]">
                            {formatCurrency(grupo.itens.reduce((sum, i) => sum + (i.valorpagar || 0), 0))}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-green-700">
                            {formatCurrency(grupo.itens.reduce((sum, i) => sum + (i.valorcomissao || 0), 0))}
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCompras;
